import { injectable } from 'inversify';
import { areIntervalsOverlapping } from 'date-fns';

import { Calendar } from '@vk-hr-tek/core/calendar';
import { CalendarItemColor, Range } from '@vk-hr-tek/ui/Calendar';
import { CalendarDiff } from '@vk-hr-tek/ui/Calendar/types';
import { FormatService } from '@vk-hr-tek/core/format';

import {
  AbsenceEmployee,
  AbsenceListGroup,
  AbsenceItem,
  AbsenceActiveNode,
  WorkScheduleCalendar,
  AbsenceUnit,
  AbsenceIntersectionMonth,
  AbsenceIntersectionDay,
} from '@app/gen/absences';
import { VacationAvailableDays } from '@app/gen/vacations';

import {
  AbsenceGroupType,
  AbsenceItemType,
  AbsencesIntersection,
  AbsencesIntersections,
} from '../types';

type ActionTypes =
  | 'generate_document_from_template'
  | 'upload'
  | 'sign'
  | 'accept';

@injectable()
export class AbsencesMapper {
  constructor(
    private calendar: Calendar,
    private format: FormatService,
  ) {}

  processEmployee(employee: AbsenceEmployee, units?: AbsenceUnit[]) {
    return {
      type: 'group',
      id: employee.unit?.id,
      name: employee.unit?.name,
      personalNumber: employee.personnel_number,
      position: employee.position,
      structure: this.processGroupStructure(units, units?.[0]?.id),
      supervisor: this.processGroupSupervisor(employee.unit),
      employeeId: employee.id,
    };
  }

  processGroupStructure(units?: AbsenceUnit[], rootId?: string): string[] {
    if (!units || !rootId) {
      return [];
    }

    const result: string[] = [];
    let nextId: string | undefined = rootId;

    const unitsMap = units?.reduce((acc: Record<string, AbsenceUnit>, curr) => {
      acc[curr.id] = curr;

      return acc;
    }, {});

    while (nextId && unitsMap[nextId]) {
      result.push(unitsMap[nextId].name);
      nextId = unitsMap[nextId].parent_id;
    }

    return result.reverse();
  }

  processGroupSupervisor(unit?: AbsenceUnit) {
    if (!unit || !unit.manager) {
      return;
    }

    const name = [
      unit.manager?.last_name || '',
      unit.manager?.first_name || '',
      unit.manager?.middle_name || '',
    ]
      .filter((el) => el)
      .join(' ');

    return {
      name,
      personalNumber: unit.manager?.personnel_number || '',
    };
  }

  private processActiveNodes(activeNodes: AbsenceActiveNode[] | undefined) {
    if (!activeNodes) return [];

    return activeNodes
      .reduce(
        (
          acc: {
            type: ActionTypes;
            deadline: string;
            isMultiple: boolean;
          }[],
          curr,
        ) => {
          if (!curr.deadline || !curr.actions) {
            return acc;
          }

          curr.actions?.forEach((action) => {
            const existingTypeIdx = acc.findIndex(
              (actionItem) => actionItem.type === action,
            );

            if (existingTypeIdx === -1) {
              const actionObj = {
                isMultiple: false,
                type: action,
                deadline: curr.deadline as string,
              };

              acc.push(actionObj);
            } else {
              acc[existingTypeIdx].isMultiple = true;
              const accDeadline = new Date(acc[existingTypeIdx].deadline);
              const currDeadline = new Date(curr.deadline as string);

              if (currDeadline < accDeadline) {
                acc[existingTypeIdx].deadline = curr.deadline as string;
              }
            }
          });

          return acc;
        },
        [],
      )
      .map((action) => {
        const deadlineDate = new Date(action.deadline);
        deadlineDate.setDate(deadlineDate.getDate() - 1);

        return {
          ...action,
          deadline: this.format.toDate(deadlineDate.toDateString()),
        };
      });
  }

  private processRangeItem(absence: AbsenceItem) {
    let color: CalendarItemColor = 'violet';

    if (
      absence.type_group === 'Отпуск' ||
      absence.type_group === 'Декретный отпуск' ||
      absence.type_group === 'Учебный отпуск'
    )
      color = 'blue';
    else if (absence.type_group === 'Командировка') color = 'orange';
    else if (absence.type_group === 'Больничный') color = 'purple';

    const start = new Date(absence.from_date);
    const end = new Date(absence.to_date);

    return {
      id: absence.type,
      eventId: absence.event_id || '',
      canCreateEvent: !!absence.event_types,
      start,
      end,
      tooltip: absence.type,
      color,
      actions: this.processActiveNodes(absence.active_nodes),
      status: absence.status,
      count: absence.days_count,
      period: `С ${this.format.toDate(
        absence.from_date,
      )} по ${this.format.toDate(absence.to_date)}`,
      ...(!!absence.intersections_count && {
        intersections: `Пересечения отсутствий: ${absence.intersections_count}`,
      }),
    };
  }

  private processRanges(absences: AbsenceItem[]) {
    const ranges: Range[] = absences
      .map((range) => this.processRangeItem(range))
      .sort((range1, range2) => {
        if (this.calendar.isSameDay(range1.end, range2.end)) {
          return range1.start > range2.start ? 1 : -1;
        } else {
          return range1.end > range2.end ? 1 : -1;
        }
      });

    let i = 0;
    const result: {
      start: Date;
      end: Date;
      ranges: Range[];
    }[] = [];

    while (i < ranges.length) {
      const currentRange = ranges[i];
      let start = currentRange.start;
      let end = currentRange.end;
      const formattedRanges = [currentRange];

      let j = i + 1;

      while (
        ranges[j] &&
        (areIntervalsOverlapping(
          { start, end },
          { start: ranges[j].start, end: ranges[j].end },
        ) ||
          this.calendar.isSameDay(end, ranges[j].start))
      ) {
        if (ranges[j].start < start) start = ranges[j].start;

        if (ranges[j].end > end) end = ranges[j].end;

        formattedRanges.push(ranges[j]);
        j++;
        i = j - 1;
      }

      result.push({
        start,
        end,
        ranges: formattedRanges,
      });
      i++;
    }

    return result;
  }

  processEmployeeAbsences(
    absences: AbsenceItem[],
    employee: AbsenceEmployee,
    employeeType: 'self' | 'colleague' | 'manager' | undefined,
    workSchedule?: WorkScheduleCalendar,
  ) {
    const calendarDiff = this.processCalendarDiff(employee, workSchedule);

    if (!absences.length) {
      return [
        {
          type: 'item',
          name: {
            fullName: employee.name,
          },
          eventId: '',
          tooltip: '',
          employeeId: employee.id,
          personalNumber: employee.personnel_number,
          ranges: [],
          calendarDiff,
          employeeType,
        },
      ];
    }

    return [
      {
        type: 'item',
        name: {
          fullName: employee.name,
        },
        tooltip: '',
        employeeId: employee.id,
        personalNumber: employee.personnel_number,
        eventId: '',
        ranges: this.processRanges(absences),
        calendarDiff,
        employeeType,
      },
    ];
  }

  processCompanyAbsences(
    absences: AbsenceItem[],
    employee: AbsenceEmployee,
    workSchedule?: WorkScheduleCalendar,
  ) {
    const calendarDiff = this.processCalendarDiff(employee, workSchedule);

    if (!absences.length) {
      return [
        {
          type: 'item',
          name: {
            fullName: employee.name,
          },
          eventId: '',
          tooltip: '',
          employeeId: employee.id,
          position: employee.position,
          personalNumber: employee.personnel_number,
          ranges: [],
          calendarDiff,
        },
      ];
    }

    return [
      {
        type: 'item',
        name: {
          fullName: employee.name,
        },
        tooltip: '',
        employeeId: employee.id,
        eventId: '',
        ranges: this.processRanges(absences),
        calendarDiff,
      },
    ];
  }

  processAbsenceClosestTask(groupes: AbsenceListGroup[]) {
    return groupes
      .filter((group) => group.type === 'self')
      .reduce((acc: AbsenceItem[] | [], { absences }) => {
        return [...acc, ...absences];
      }, [])
      .sort((a, b) => {
        return a.from_date > b.from_date ? 1 : -1;
      })
      .filter(
        (item) =>
          !item.event_id &&
          item.is_scheduled_vacation &&
          item.status === 'new' &&
          new Date() < new Date(item.from_date),
      )
      .map((item) => {
        return { ...item, type: item.type };
      })[0];
  }

  processEmployeeGroups(groups: AbsenceListGroup[]) {
    return groups.reduce(
      (
        acc: (AbsenceItemType | AbsenceGroupType)[],
        {
          type: employeeType,
          employee,
          absences,
          units,
          work_schedule: workSchedule,
        },
      ) => {
        if (employeeType !== 'manager') {
          const group = this.processEmployee(employee, units);

          const groupAlreadyInData = acc.find(
            (item) =>
              item.type === group.type &&
              (item as AbsenceGroupType).id === group.id,
          );

          if (!groupAlreadyInData) {
            acc.push(group);
          }
        }

        acc.push(
          ...this.processEmployeeAbsences(
            absences,
            employee,
            employeeType,
            workSchedule,
          ),
        );

        return acc;
      },
      [],
    );
  }

  processCompanyGroups(groups: AbsenceListGroup[]) {
    return groups.reduce(
      (
        acc: (AbsenceItemType | AbsenceGroupType)[],
        { employee, absences, units, work_schedule: workSchedule },
      ) => {
        const group: AbsenceGroupType = this.processEmployee(employee, units);

        const groupAlreadyInData = acc.find(
          (item) =>
            item.type === group.type &&
            (item as AbsenceGroupType).id === group.id,
        );

        if (!groupAlreadyInData) {
          acc.push(group);
        }

        acc.push(
          ...this.processCompanyAbsences(absences, employee, workSchedule),
        );

        return acc;
      },
      [],
    );
  }

  processEmployeeCalendarDiff(employee: AbsenceEmployee): CalendarDiff {
    return (employee.calendar_diff || []).reduce((acc: CalendarDiff, curr) => {
      acc[curr.date] = {
        workday: curr.type === 'workday',
        holiday: curr.type === 'holiday',
      };

      return acc;
    }, {});
  }

  processWorkScheduleCalendar(
    workSchedule: WorkScheduleCalendar,
  ): CalendarDiff {
    return (workSchedule.dates || []).reduce((acc: CalendarDiff, curr) => {
      acc[curr.date] = {
        workday: curr.is_workday,
        holiday: curr.is_holiday,
      };

      return acc;
    }, {});
  }

  processCalendarDiff(
    employee: AbsenceEmployee,
    workSchedule?: WorkScheduleCalendar,
  ): CalendarDiff {
    if (workSchedule) {
      return this.processWorkScheduleCalendar(workSchedule);
    }

    return this.processEmployeeCalendarDiff(employee);
  }

  processDayIntersections(intersections: AbsenceIntersectionDay[]) {
    return intersections.reduce<Record<number, AbsencesIntersection>>(
      (acc, intersection) => {
        const day = new Date(intersection.date).getDate();

        acc[day] = {
          total: intersection.total,
          quantity: intersection.fraction,
        };

        return acc;
      },
      {},
    );
  }

  processMonthIntersections(intersections: AbsenceIntersectionMonth[]) {
    return intersections.reduce<AbsencesIntersections>((acc, intersection) => {
      acc[intersection.month] = {
        total: intersection.total,
        quantity: intersection.fraction,
        days: this.processDayIntersections(intersection.days),
      };

      return acc;
    }, {});
  }

  processAvailableDays(availableDays: VacationAvailableDays[]) {
    return {
      availableDays: availableDays.map((availableDay) => ({
        ...availableDay,
        periods: availableDay.periods?.length
          ? availableDay.periods.map(({ period_start, period_end, days }) => ({
              dateRange: `${this.format.toDate(period_start)}г. - ${this.format.toDate(period_end)}г.`,
              days,
            }))
          : [],
      })),
      hasPeriods: availableDays.some(
        (availableDay) => (availableDay.periods?.length || 0) > 0,
      ),
    };
  }
}
